import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X, CreditCard, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

type LockReason = 'freemium' | 'monthly_incomplete' | 'monthly_locked';

interface LockedContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType?: 'module' | 'class';
  lockReason?: LockReason;
  previousModuleNumber?: number;
}

const LockedContentModal = ({ 
  isOpen, 
  onClose, 
  contentType = 'class',
  lockReason = 'freemium',
  previousModuleNumber
}: LockedContentModalProps) => {
  const navigate = useNavigate();

  const handleSubscribe = () => {
    onClose();
    navigate('/profile', { state: { openSubscription: true } });
  };

  // Different messaging based on lock reason
  const getContent = () => {
    if (lockReason === 'monthly_incomplete') {
      return {
        title: 'Complete Previous Module',
        description: `Complete 100% of Module ${previousModuleNumber || 'previous'} to unlock this ${contentType}. Finish all classes and pass the exercises to continue your journey!`,
        showSubscribe: false,
        icon: <Lock className="w-8 h-8 text-blue-600" />,
        benefits: [
          'Complete all classes in the current module',
          'Pass all exercises with 70%+ score',
          'The next module will unlock automatically',
          'Track your progress on the dashboard'
        ]
      };
    }
    
    return {
      title: 'Content Locked',
      description: `This ${contentType} is locked. Subscribe now to unlock full access to all modules and classes!`,
      showSubscribe: true,
      icon: <Lock className="w-8 h-8 text-blue-600" />,
      benefits: [
        'All 50 modules across 5 academies',
        '500+ classes with video lessons',
        'Interactive exercises & quizzes',
        'Completion certificates'
      ]
    };
  };

  const content = getContent();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200 rounded-2xl">
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            {content.icon}
          </div>
          <DialogTitle className="text-2xl font-bold tracking-wider text-center text-slate-900">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-center text-slate-600 mt-2">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-6">
          {/* Benefits/Requirements */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <h4 className="text-sm font-medium text-blue-600 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {content.showSubscribe ? "What you'll unlock:" : "How to unlock:"}
            </h4>
            <ul className="space-y-2 text-sm text-slate-600">
              {content.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="text-blue-600">✦</span>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3">
            {content.showSubscribe ? (
              <Button
                onClick={handleSubscribe}
                className="w-full py-6 text-base bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Subscribe Now
              </Button>
            ) : (
              <Button
                onClick={onClose}
                className="w-full py-6 text-base bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              >
                Got it, I'll complete the module
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LockedContentModal;